/**
 * Offline bodies and kidnapping (ร่างออฟไลน์ · ลักพาตัว, docs/systems/OFFLINE_WORK.md, data/offlineWork.ts).
 *
 * - **Offline bodies.** A zone's bodies (`OFFLINE_BODIES`) stand at their job's spot doing its act. In this
 *   single-player build they stand in for other players who logged off (as the arena's fighters do); a
 *   server would make one of a disconnected player instead (docs/ONLINE.md §8).
 * - **The blow (เป่ายาสลบ).** `blowPowder` at a body within 2 m, with knockout powder in the pouch: a 1.5 s
 *   wind-up (`e.blow`, motion `blowPowder`), broken off by moving, fighting or being struck. Done, the body
 *   becomes a sack (`e.sack`, look `prop:sack`, motion `sackIdle`) and the powder is spent.
 * - **The sack.** `sackPickup` shoulders it (`e.carrying` / `e.carriedBy`, motion `carrySack`, the status
 *   `sack-carry`: ×0.6 pace, no qinggong, no mount); `sackDrop` puts it down, as any blow taken does; a
 *   player who strikes the carrier frees it and is paid. `sackSell` beside a black-market buyer pays by the
 *   victim's rank and how far it was carried. Unsold, a sack wakes after 5 minutes.
 * - **The rules.** Not in the zone's guarded places (`NO_KIDNAP`, else its PK-safe outline), a school's
 *   compound or indoors; not a victim below the rank floor; not one taken in the last 10 minutes.
 *
 * Also sets the character down where it woke after the time away (offlineWork.ts `settleAway`), and takes
 * `offlineJob` (the job picked for the next time away) and `blackBuy` (the buyers' knockout powder).
 * Draws nothing from the World's `Rng`: the same commands give the same outcome on every machine.
 */
import { coreOf } from '../region/core';
import { rankFor } from '../cultivation';
import {
  buyersIn,
  INFAMY,
  isOfflineJobId,
  KIDNAP,
  NO_KIDNAP,
  OFFLINE_BODIES,
  OFFLINE_JOBS,
  rescueReward,
  sackPrice,
  SACK_BUYERS,
  spotsFor,
  type OfflineJobId,
  type SackBuyer,
} from '../data/offlineWork';
import { GOODS } from '../data/professions';
import { BLACK_MARKET } from '../data/shop';
import { ZONES } from '../data/zones';
import { isInfamous, offlineOf, shunnedByGuards } from '../offlineWork';
import { FOLK_ROLES } from '../data/npcActs';
import { freeAt, nearestFloor } from '../path';
import { dist, type Entity, type PlayerCommand, type World, type WorldEvent } from '../world';
import { actOf, clearAct, setAct } from './npcActs';
import type { SystemHost, WorldSystem } from './types';

/** The motion a figure plays for kidnapping, over everything else (lane OFF-art's ids). */
export type KidnapMotion = 'blowPowder' | 'carrySack' | 'sackIdle';

declare module '../world' {
  interface Entity {
    /** An offline body (ร่างออฟไลน์): its job, power rank, and the World time it is guarded to after a kidnapping. */
    offline?: { job: OfflineJobId; rank: number; guardUntil: number } | null;
    /** Knocked out and tied in a sack (drawn as `prop:sack`). */
    sack?: boolean;
    /** A sack: who carries it (entity id), or null on the ground. */
    carriedBy?: number | null;
    /** The sack this figure carries (entity id), or null. */
    carrying?: number | null;
    /** Blowing knockout powder: at whom, and seconds into the blow (`KIDNAP.blowTime`). */
    blow?: { tgtId: number; t: number } | null;
    /** The kidnapping motion to play, or null. */
    kidnapMotion?: KidnapMotion | null;
    /** ชื่อฉาว at or past `INFAMY.red`: the name tag reads red (the player; a server sets it on every player). */
    infamous?: boolean;
  }
}

export type BlowCancel = 'moved' | 'hit' | 'acted' | 'gone' | 'dead';
export type SackDropReason = 'put' | 'hit' | 'dead' | 'woke';

/** Commands (part of `PlayerCommand`). */
export type OfflineWorkCommand =
  /** The job for the next time away (null: the one the best trade suits). */
  | { t: 'offlineJob'; job: OfflineJobId | null; goods?: boolean }
  /** Blow knockout powder at an offline body. */
  | { t: 'blowPowder'; id: number }
  /** Shoulder a sack, put it down, or sell it to the buyer in reach. */
  | { t: 'sackPickup'; id: number }
  | { t: 'sackDrop' }
  | { t: 'sackSell' }
  /** Buy from a black-market buyer in reach (data/shop.ts `BLACK_MARKET`). */
  | { t: 'blackBuy'; good: string };

/** Events (part of `WorldEvent`). Sounds (lane OFF-art): `blowPowder` on blowStart, `sackDrop`, `coinSale` on sackSold. */
export type OfflineWorkEvent =
  | { type: 'offlineJob'; job: OfflineJobId | null }
  | { type: 'blowStart'; id: number; tgtId: number }
  | { type: 'blowCancel'; id: number; tgtId: number; reason: BlowCancel }
  | { type: 'sacked'; id: number; tgtId: number }
  | { type: 'sackPickup'; id: number; sackId: number }
  | { type: 'sackDrop'; id: number; sackId: number; reason: SackDropReason }
  | { type: 'sackSold'; id: number; sackId: number; buyer: string; price: number }
  | { type: 'sackRescued'; id: number; sackId: number; carrierId: number; reward: number }
  | { type: 'sackWoke'; sackId: number };

interface Blow {
  tgtId: number;
  t: number;
  x: number;
  y: number;
}

interface Sack {
  /** Where it was taken (the price grows with the distance from here). */
  fromX: number;
  fromY: number;
  wakeAt: number;
  carrier: number | null;
}

const SYSTEMS = new WeakMap<World, OfflineWorkSystem>();

/** The World's offline-work system (for the client's prompts and for tests). */
export const offlineWorkOf = (w: World): OfflineWorkSystem => {
  const s = SYSTEMS.get(w);
  if (!s) throw new Error('offline work system not set up');
  return s;
};

/** The commands that break off a blow under way (the blower acted). */
const ACTS = new Set<PlayerCommand['t']>(['attack', 'skill', 'jump', 'dash', 'mount', 'meditate', 'emote']);
/** What a carrier cannot do (the status `sack-carry` already seals qinggong). */
const CARRY_BARS = new Set<PlayerCommand['t']>([
  'attack',
  'skill',
  'mount',
  'fly',
  'meditate',
  'travel',
  'enter',
  'carriageBoard',
  'danceStart',
  'danceJoin',
  'arenaFight',
  'ladderJoin',
]);

export class OfflineWorkSystem implements WorldSystem {
  readonly name = 'offlineWork';
  /** Offline bodies here by id, with their work spot. */
  private readonly bodies = new Map<number, { x: number; y: number }>();
  /** Black-market buyers here by entity id. */
  private readonly buyers = new Map<number, SackBuyer>();
  private readonly blows = new Map<number, Blow>();
  private readonly sacks = new Map<number, Sack>();

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    offlineJob: (c: { job: OfflineJobId | null; goods?: boolean }) => this.pickJob(c.job, c.goods),
    blowPowder: (c: { id: number }) => this.startBlow(this.h.world.player, c.id),
    sackPickup: (c: { id: number }) => this.pickup(this.h.world.player, c.id),
    sackDrop: () => this.drop(this.h.world.player, 'put'),
    sackSell: () => this.sell(this.h.world.player),
    blackBuy: (c: { good: string }) => this.buy(c.good),
  };

  setup(): void {
    const w = this.h.world;
    SYSTEMS.set(w, this);
    const zone = w.map.zone;
    for (const b of OFFLINE_BODIES) {
      if (b.zone !== zone) continue;
      const spot = spotsFor(zone, b.job)[0];
      const at = spot && this.floor(spot.pos[0], spot.pos[1]);
      if (!at) continue;
      const e = this.h.add({
        kind: 'npc',
        x: at.x,
        y: at.y,
        name: b.name,
        title: `ออฟไลน์ · ${OFFLINE_JOBS[b.job].name}`,
        sprite: b.sprite,
        facing: at.x < w.map.plaza.x ? 1 : -1,
      });
      e.offline = { job: b.job, rank: b.rank, guardUntil: 0 };
      this.bodies.set(e.id, at);
      setAct(w, e, OFFLINE_JOBS[b.job].act, 3600);
    }
    for (const b of buyersIn(zone)) {
      const at = this.floor(b.pos[0], b.pos[1]);
      if (!at) continue;
      const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, name: b.name, title: b.title, sprite: b.sprite });
      this.buyers.set(e.id, b);
    }
    this.wake();
  }

  /** Sets the character down where it woke after the time away (offlineWork.ts `settleAway`), once. */
  private wake(): void {
    const w = this.h.world;
    const st = w.progress.offline;
    if (!st?.wake) return;
    const wake = st.wake;
    st.wake = null;
    let at: { x: number; y: number } | null = null;
    if (wake.at === 'buyer') {
      const b = SACK_BUYERS.find((x) => x.id === wake.buyer && x.zone === w.map.zone);
      if (b) at = this.floor(b.pos[0] + 1.2, b.pos[1] + 0.8) ?? this.floor(b.pos[0], b.pos[1] + 1.5);
    } else {
      const s = spotsFor(w.map.zone, wake.job)[0];
      if (s) at = this.floor(s.pos[0] + 0.9, s.pos[1] + 0.6) ?? this.floor(s.pos[0], s.pos[1]);
    }
    if (!at) return;
    const p = w.player;
    Object.assign(p, { x: at.x, y: at.y, path: null });
    Object.assign(w.pet, { x: at.x + 0.8, y: at.y - 0.5 });
  }

  private floor(x: number, y: number): { x: number; y: number } | null {
    const m = this.h.world.map;
    // The spots are in the town's own metres; in a region the town sits at the core's origin.
    const o = coreOf(m);
    x += o.x;
    y += o.y;
    if (x < 0 || y < 0 || x >= m.w || y >= m.h) return null;
    if (freeAt(m, x, y)) return { x, y };
    const a = nearestFloor(m, x, y, 3);
    return a ? { x: a[0], y: a[1] } : null;
  }

  // ───────────────────────── queries (the client's prompts) ─────────────────────────

  /** The offline bodies here (sacked or not). */
  bodyIds(): number[] {
    return [...this.bodies.keys()];
  }

  /** The black-market buyer entity ids here, with their data. */
  buyerList(): { id: number; buyer: SackBuyer }[] {
    return [...this.buyers].map(([id, buyer]) => ({ id, buyer }));
  }

  /** A buyer within selling reach of `e`, or null. */
  buyerNear(e: Entity): { id: number; buyer: SackBuyer } | null {
    for (const [id, buyer] of this.buyers) {
      const b = this.h.world.get(id);
      if (b && dist(e, b) <= KIDNAP.sellRange) return { id, buyer };
    }
    return null;
  }

  /** The price a sack would fetch at a buyer entity (null if it is not a sack or not a buyer). */
  priceAt(sackId: number, buyerId: number): number | null {
    const s = this.sacks.get(sackId);
    const e = this.h.world.get(sackId);
    const b = this.h.world.get(buyerId);
    if (!s || !e?.offline || !b || !this.buyers.has(buyerId)) return null;
    return sackPrice(e.offline.rank, Math.hypot(b.x - s.fromX, b.y - s.fromY));
  }

  /** Seconds before a sack wakes, or null. */
  wakeIn(sackId: number): number | null {
    const s = this.sacks.get(sackId);
    return s ? Math.max(0, s.wakeAt - this.h.world.time) : null;
  }

  /** Kidnapping is barred at this point (a guarded place, a school compound, indoors). */
  guarded(at: { x: number; y: number }): boolean {
    const w = this.h.world;
    if (w.interiors.inside) return true;
    const zone = w.map.zone;
    if (ZONES[zone as keyof typeof ZONES]?.kind === 'school') return true;
    const areas = NO_KIDNAP.filter((a) => a.zone === zone);
    const o = coreOf(w.map);
    if (areas.length) return areas.some((a) => Math.hypot(at.x - o.x - a.at[0], at.y - o.y - a.at[1]) <= a.r);
    return w.inSafeZone(at);
  }

  /** Why `by` cannot blow powder at `tgt` now (Thai, for a notice or the prompt), or null when it can. */
  blowBlock(by: Entity, tgt: Entity | undefined): string | null {
    const w = this.h.world;
    if (!tgt?.offline || tgt.sack) return 'เป่ายาสลบได้เฉพาะร่างออฟไลน์';
    if (by.dead) return 'หมดสติอยู่';
    if (by.carrying) return 'วางกระสอบก่อน';
    if (by.mount) return 'ลงจากพาหนะก่อน';
    if (this.blows.has(by.id)) return 'กำลังเป่ายาสลบอยู่';
    if (w.inArena()) return 'ใช้ในลานประลองไม่ได้';
    if (dist(by, tgt) > KIDNAP.blowRange) return `เข้าไปให้ใกล้กว่า ${KIDNAP.blowRange} เมตร`;
    if (this.guarded(tgt) || this.guarded(by)) return 'ที่นี่มีคนเฝ้า ลักพาตัวไม่ได้';
    if (tgt.offline.rank < KIDNAP.rankFloor) return 'ผู้ฝึกยุทธ์หน้าใหม่ได้รับการคุ้มครอง';
    if (tgt.offline.guardUntil > w.time) return 'เพิ่งถูกลักพาตัวไป ยังระวังตัวอยู่';
    if (by === w.player && (w.progress.trades.goods['knockout-powder'] ?? 0) < 1) return 'ไม่มียาสลบ';
    return null;
  }

  // ───────────────────────── commands ─────────────────────────

  private pickJob(job: OfflineJobId | null, goods = false): void {
    if (job !== null && !isOfflineJobId(job)) return;
    const st = offlineOf(this.h.world.progress);
    st.job = job;
    st.goods = goods === true;
    this.h.emit({ type: 'offlineJob', job });
    this.h.notice(job ? `งานยามออฟไลน์: ${OFFLINE_JOBS[job].name}` : 'งานยามออฟไลน์: ตามอาชีพที่ถนัด');
  }

  startBlow(by: Entity, id: number): void {
    const tgt = this.h.world.get(id);
    const why = this.blowBlock(by, tgt);
    if (why || !tgt) {
      if (why && by === this.h.world.player) this.h.notice(why);
      return;
    }
    this.h.stopMeditating();
    if (by === this.h.world.player) Object.assign(this.h.input, { dx: 0, dy: 0, engage: false, talkTo: null });
    by.path = null;
    this.h.face(by, tgt);
    this.blows.set(by.id, { tgtId: id, t: 0, x: by.x, y: by.y });
    by.blow = { tgtId: id, t: 0 };
    by.kidnapMotion = 'blowPowder';
    this.h.emit({ type: 'blowStart', id: by.id, tgtId: id });
  }

  private cancelBlow(id: number, reason: BlowCancel): void {
    const b = this.blows.get(id);
    if (!b) return;
    this.blows.delete(id);
    const e = this.h.world.get(id);
    if (e) {
      e.blow = null;
      if (e.kidnapMotion === 'blowPowder') e.kidnapMotion = null;
    }
    this.h.emit({ type: 'blowCancel', id, tgtId: b.tgtId, reason });
    if (e === this.h.world.player && reason !== 'gone') this.h.notice('การเป่ายาสลบถูกขัดจังหวะ');
  }

  private finishBlow(by: Entity, tgt: Entity): void {
    const w = this.h.world;
    this.blows.delete(by.id);
    by.blow = null;
    by.kidnapMotion = null;
    if (by === w.player) {
      const g = w.progress.trades.goods;
      g['knockout-powder'] = (g['knockout-powder'] ?? 0) - 1;
      if (g['knockout-powder']! <= 0) delete g['knockout-powder'];
    }
    clearAct(tgt);
    tgt.sack = true;
    tgt.carriedBy = null;
    tgt.kidnapMotion = 'sackIdle';
    this.sacks.set(tgt.id, { fromX: tgt.x, fromY: tgt.y, wakeAt: w.time + KIDNAP.wakeAfter, carrier: null });
    this.h.emit({ type: 'sacked', id: by.id, tgtId: tgt.id });
    if (by === w.player) this.h.notice(`${tgt.name} หลับไปแล้ว ถูกมัดใส่กระสอบ · อุ้มไปขายพ่อค้าตลาดมืด`);
  }

  pickup(by: Entity, id: number): void {
    const s = this.sacks.get(id);
    const e = this.h.world.get(id);
    const me = by === this.h.world.player;
    const no = (t: string) => void (me && this.h.notice(t));
    if (!s || !e?.sack) return no('ไม่ใช่กระสอบ');
    if (s.carrier !== null) return no('มีคนแบกกระสอบนี้อยู่');
    if (by.dead || by.carrying || this.blows.has(by.id)) return;
    if (dist(by, e) > KIDNAP.pickRange) return no(`เข้าไปให้ใกล้กว่า ${KIDNAP.pickRange} เมตร`);
    if (by.mount && me) this.h.dismount();
    this.h.stopMeditating();
    s.carrier = by.id;
    by.carrying = id;
    by.kidnapMotion = 'carrySack';
    e.carriedBy = by.id;
    this.follow(by, e);
    this.carryStatus(by, true);
    this.h.emit({ type: 'sackPickup', id: by.id, sackId: id });
  }

  /** Puts down the sack `by` carries (put down, struck, fallen, or the sack woke). */
  drop(by: Entity, reason: SackDropReason): void {
    const id = by.carrying;
    if (id === null || id === undefined) return;
    const s = this.sacks.get(id);
    const e = this.h.world.get(id);
    by.carrying = null;
    if (by.kidnapMotion === 'carrySack') by.kidnapMotion = null;
    this.carryStatus(by, false);
    if (s) s.carrier = null;
    if (e) {
      e.carriedBy = null;
      e.z = 0;
      const fx = by.x + by.dirX * 0.6;
      const fy = by.y + by.dirY * 0.6;
      const at = freeAt(this.h.world.map, fx, fy) ? { x: fx, y: fy } : { x: by.x, y: by.y };
      e.x = at.x;
      e.y = at.y;
    }
    this.h.emit({ type: 'sackDrop', id: by.id, sackId: id, reason });
    if (by === this.h.world.player && reason === 'hit') this.h.notice('ถูกโจมตี กระสอบหล่นจากบ่า');
  }

  private sell(by: Entity): void {
    const w = this.h.world;
    const id = by.carrying;
    const me = by === w.player;
    if (id === null || id === undefined) return void (me && this.h.notice('ไม่ได้แบกกระสอบ'));
    const near = this.buyerNear(by);
    if (!near) return void (me && this.h.notice('ต้องแบกไปขายให้พ่อค้าตลาดมืด'));
    const price = this.priceAt(id, near.id) ?? 0;
    const e = w.get(id);
    this.drop(by, 'put');
    if (me) {
      w.progress.gold += price;
      const st = offlineOf(w.progress);
      st.infamy += KIDNAP.infamy;
      st.sold++;
    }
    this.release(id);
    this.h.emit({ type: 'sackSold', id: by.id, sackId: id, buyer: near.buyer.id, price });
    if (me) {
      this.h.notice(`ขาย${e?.name ?? 'กระสอบ'}ให้${near.buyer.name} ได้ ${price.toLocaleString('en-US')} ตำลึง · ชื่อฉาว +${KIDNAP.infamy}`);
      this.h.log('sys', `ขายกระสอบ (${e?.name ?? ''}) ให้${near.buyer.name} ได้ ${price} ตำลึง`);
    }
  }

  private buy(good: string): void {
    const w = this.h.world;
    const p = w.player;
    const entry = BLACK_MARKET.find((x) => x.good === good);
    if (!entry || !GOODS[good] || p.dead) return;
    if (!this.buyerNear(p)) return this.h.notice('ต้องซื้อจากพ่อค้าตลาดมืด');
    if (w.progress.gold < entry.price) return this.h.notice('ตำลึงไม่พอ');
    w.progress.gold -= entry.price;
    const g = w.progress.trades.goods;
    g[good] = (g[good] ?? 0) + 1;
    this.h.notice(`ซื้อ${GOODS[good].name} 1 ห่อ (${entry.price} ตำลึง)`);
  }

  // ───────────────────────── the sack's end ─────────────────────────

  /** The kidnapping is over (sold, woke, freed): the body is back at work, guarded for a while. */
  private release(id: number): void {
    const w = this.h.world;
    this.sacks.delete(id);
    const e = w.get(id);
    if (!e) return;
    const carrier = e.carriedBy != null ? w.get(e.carriedBy) : undefined;
    if (carrier) this.drop(carrier, 'woke');
    e.sack = false;
    e.carriedBy = null;
    e.kidnapMotion = null;
    e.z = 0;
    const home = this.bodies.get(id);
    if (home) Object.assign(e, { x: home.x, y: home.y });
    if (e.offline) {
      e.offline.guardUntil = w.time + KIDNAP.cooldown;
      setAct(w, e, OFFLINE_JOBS[e.offline.job].act, 3600);
    }
  }

  private wakeSack(id: number): void {
    const e = this.h.world.get(id);
    const carrier = e?.carriedBy != null ? this.h.world.get(e.carriedBy) : undefined;
    if (carrier) this.drop(carrier, 'woke');
    this.release(id);
    this.h.emit({ type: 'sackWoke', sackId: id });
    if (carrier === this.h.world.player) this.h.notice(`${e?.name ?? 'คนในกระสอบ'} ตื่นแล้ว ดิ้นหลุดหนีไป`);
  }

  private rescue(by: Entity, carrier: Entity): void {
    const w = this.h.world;
    const id = carrier.carrying;
    if (id === null || id === undefined) return;
    const e = w.get(id);
    const reward = rescueReward(e?.offline?.rank ?? 0);
    this.drop(carrier, 'hit');
    this.release(id);
    if (by === w.player) w.progress.gold += reward;
    this.h.emit({ type: 'sackRescued', id: by.id, sackId: id, carrierId: carrier.id, reward });
    if (by === w.player) this.h.notice(`ช่วย${e?.name ?? 'คนในกระสอบ'}ไว้ได้ · รับ ${reward} ตำลึง`);
  }

  // ───────────────────────── hooks ─────────────────────────

  /** Speaking to a buyer (their wares, or an offer for the sack carried) or to an offline body. */
  talk(npc: Entity): boolean {
    const w = this.h.world;
    if (npc.folk && FOLK_ROLES[npc.folk.id] === 'guard' && shunnedByGuards(w.progress)) {
      this.h.emit({ type: 'dialog', npcId: npc.id, text: 'คนชื่อฉาวอย่างเจ้า ข้าไม่มีอะไรจะพูดด้วย ไปให้พ้น', action: null });
      return true;
    }
    const buyer = this.buyers.get(npc.id);
    if (buyer) {
      const id = w.player.carrying;
      const price = id != null ? this.priceAt(id, npc.id) : null;
      const powder = BLACK_MARKET.map((x) => `${GOODS[x.good]?.name ?? x.good} ห่อละ ${x.price} ตำลึง`).join(' · ');
      const text =
        price !== null
          ? `ของดีนี่... กระสอบนี้ข้าให้ ${price.toLocaleString('en-US')} ตำลึง ยิ่งแบกมาไกลยิ่งได้ราคา`
          : `ข้ามี${powder} ได้คนมาก็แบกมาขายข้า ยิ่งไกลจากที่จับมายิ่งได้ราคา`;
      this.h.emit({ type: 'dialog', npcId: npc.id, text, action: null });
      return true;
    }
    if (npc.offline) {
      const text = npc.sack
        ? `กระสอบที่${npc.name}หลับอยู่ข้างใน`
        : `ร่างออฟไลน์ของ${npc.name} กำลัง${OFFLINE_JOBS[npc.offline.job].name}อยู่ ตัวจริงไม่อยู่ในยุทธภพตอนนี้`;
      this.h.emit({ type: 'dialog', npcId: npc.id, text, action: null });
      return true;
    }
    return false;
  }

  /** A blow is broken off by the blower acting; a carrier cannot fight, ride, meditate or leave the map. */
  refuse(cmd: PlayerCommand): boolean {
    const p = this.h.world.player;
    if (this.blows.has(p.id)) {
      const moving =
        (cmd.t === 'moveDir' && (cmd.dx || cmd.dy)) || cmd.t === 'moveTo' || cmd.t === 'engage' || cmd.t === 'interact';
      if (moving || ACTS.has(cmd.t)) this.cancelBlow(p.id, moving ? 'moved' : 'acted');
    }
    if (p.carrying != null && CARRY_BARS.has(cmd.t)) {
      this.h.notice('แบกกระสอบอยู่ ทำอย่างอื่นไม่ได้ · วางกระสอบก่อน');
      return true;
    }
    return false;
  }

  observe(ev: WorldEvent): void {
    if (ev.type === 'hit') {
      if (!(ev.damage > 0)) return;
      if (this.blows.has(ev.tgtId)) this.cancelBlow(ev.tgtId, 'hit');
      const c = this.h.world.get(ev.tgtId);
      if (c?.carrying == null) return;
      const by = this.h.world.get(ev.srcId);
      if (by && by !== c && (by.kind === 'player' || by.kind === 'rival')) this.rescue(by, c);
      else this.drop(c, 'hit');
    } else if (ev.type === 'death') {
      if (this.blows.has(ev.id)) this.cancelBlow(ev.id, 'dead');
      const c = this.h.world.get(ev.id);
      if (c?.carrying != null) this.drop(c, 'dead');
    }
  }

  step(dt: number): void {
    const w = this.h.world;
    this.fade(dt);
    for (const [id, b] of [...this.blows]) {
      const by = w.get(id);
      const tgt = w.get(b.tgtId);
      if (!by || by.dead) {
        this.cancelBlow(id, 'dead');
        continue;
      }
      if (!tgt?.offline || tgt.sack) {
        this.cancelBlow(id, 'gone');
        continue;
      }
      if (Math.hypot(by.x - b.x, by.y - b.y) > KIDNAP.blowDrift) {
        this.cancelBlow(id, 'moved');
        continue;
      }
      b.t += dt;
      by.blow = { tgtId: b.tgtId, t: b.t };
      if (b.t >= KIDNAP.blowTime) this.finishBlow(by, tgt);
    }
    for (const [id, s] of [...this.sacks]) {
      const e = w.get(id);
      // Set aside with the zone's people while the player is indoors: it waits.
      if (!e) continue;
      if (s.carrier !== null) {
        const c = w.get(s.carrier);
        if (!c || c.dead) {
          if (c) this.drop(c, 'dead');
          else s.carrier = null;
        } else {
          this.follow(c, e);
          this.carryStatus(c, true);
        }
      }
      if (w.time >= s.wakeAt) this.wakeSack(id);
    }
    // The bodies keep at their work.
    for (const id of this.bodies.keys()) {
      const e = w.get(id);
      if (e?.offline && !e.sack && !actOf(w, e)) setAct(w, e, OFFLINE_JOBS[e.offline.job].act, 3600);
    }
  }

  /** ชื่อฉาว fades a point per `INFAMY.decaySeconds` of play; the player's tag follows it. */
  private fade(dt: number): void {
    const w = this.h.world;
    const st = w.progress.offline;
    if (st && st.infamy > 0 && (st.infamyT += dt) >= INFAMY.decaySeconds) {
      st.infamyT = 0;
      st.infamy--;
    }
    const red = isInfamous(w.progress);
    if (!!w.player.infamous !== red) w.player.infamous = red;
  }

  /**
   * What the interact key (E) does here, if anything kidnapping offers, and how far away it is (the client lets
   * the nearer of this and a door win): sell to the buyer in reach or put the sack down while carrying, pick up
   * a sack, or blow powder at an offline body that can be taken. Null mid-blow, and when there is nothing.
   */
  useAction(by: Entity): { cmd: PlayerCommand; d: number; label: string } | null {
    const w = this.h.world;
    if (by.dead || this.blows.has(by.id)) return null;
    if (by.carrying != null) {
      const near = this.buyerNear(by);
      if (near) {
        const b = w.get(near.id)!;
        return { cmd: { t: 'sackSell' }, d: dist(by, b), label: `ขายให้${near.buyer.name}` };
      }
      return { cmd: { t: 'sackDrop' }, d: 0, label: 'วางกระสอบ' };
    }
    let best: { cmd: PlayerCommand; d: number; label: string } | null = null;
    for (const id of this.bodies.keys()) {
      const e = w.get(id);
      if (!e) continue;
      const d = dist(by, e);
      if (best && d >= best.d) continue;
      if (e.sack && e.carriedBy == null && d <= KIDNAP.pickRange)
        best = { cmd: { t: 'sackPickup', id }, d, label: `อุ้ม${e.name}` };
      else if (!e.sack && d <= KIDNAP.blowRange && !this.blowBlock(by, e))
        best = { cmd: { t: 'blowPowder', id }, d, label: 'เป่ายาสลบ' };
    }
    return best;
  }

  /** A carried sack rides on the carrier's shoulder. */
  private follow(c: Entity, e: Entity): void {
    e.x = c.x;
    e.y = c.y;
    e.z = c.z;
    e.facing = c.facing;
    e.heading = c.heading;
  }

  /** Keeps the carrier's `sack-carry` status on (refreshed every step), or takes it off. */
  private carryStatus(c: Entity, on: boolean): void {
    const i = c.statuses.findIndex((s) => s.id === 'sack-carry');
    if (on) {
      if (i >= 0) c.statuses[i]!.t = 1;
      else {
        c.statuses.push({ id: 'sack-carry', stacks: 1, t: 1 });
        this.h.emit({ type: 'status', id: c.id, status: 'sack-carry', stacks: 1 });
      }
    } else if (i >= 0) {
      c.statuses.splice(i, 1);
      this.h.emit({ type: 'status', id: c.id, status: 'sack-carry', stacks: 0 });
    }
  }
}

/** The player's power rank (for the report and the UI). */
export const playerRank = (w: World): number => rankFor(w.progress.cultivationSpent).index;
